import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, TextField, Button, Grid, Divider, 
  CircularProgress, Alert, Switch, FormControlLabel, Chip, 
  IconButton, Select, MenuItem, FormControl, InputLabel 
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { getSettings, updateSettings, getAllCategories, createCategory, deleteCategory } from '../api/apiService';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    email: '',
    contactNumber: '',
    address: '',
    facebook: '',
    linkedin: '',
    twitter: '',
    instagram: '',
    faqActive: true,
    supportPhone: '',
    supportEmail: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  // Category State
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', type: 'Both' });
  const [categoryLoading, setCategoryLoading] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await getSettings();
      if (response && response.data) {
        const data = response.data;
        setSettings({
          email: data.email || '',
          contactNumber: data.contactNumber || '',
          address: data.address || '',
          facebook: data.socialLinks?.facebook || '',
          linkedin: data.socialLinks?.linkedin || '',
          twitter: data.socialLinks?.twitter || '',
          instagram: data.socialLinks?.instagram || '',
          faqActive: data.faqActive !== undefined ? data.faqActive : true,
          supportPhone: data.supportPhone || '',
          supportEmail: data.supportEmail || ''
        });
      }
      
      // Fetch categories too
      const catResponse = await getAllCategories();
      if (catResponse && catResponse.data) {
        setCategories(catResponse.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setStatus({ type: 'error', message: 'Failed to load settings.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) return;
    setCategoryLoading(true);
    try {
      const response = await createCategory(newCategory);
      if (response.success) {
        setCategories([...categories, response.data]);
        setNewCategory({ name: '', type: 'Both' });
        setStatus({ type: 'success', message: 'Category added successfully!' });
      }
    } catch (error) {
      console.error('Error adding category:', error);
      setStatus({ type: 'error', message: error.response?.data?.message || 'Failed to add category.' });
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      const response = await deleteCategory(id);
      if (response.success) {
        setCategories(categories.filter(cat => cat.id !== id));
        setStatus({ type: 'success', message: 'Category deleted successfully!' });
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      setStatus({ type: 'error', message: error.response?.data?.message || 'Failed to delete category.' });
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus({ type: '', message: '' });
    try {
      const payload = {
        email: settings.email,
        contactNumber: settings.contactNumber,
        address: settings.address,
        socialLinks: {
          facebook: settings.facebook,
          linkedin: settings.linkedin,
          twitter: settings.twitter,
          instagram: settings.instagram
        },
        faqActive: settings.faqActive,
        supportPhone: settings.supportPhone,
        supportEmail: settings.supportEmail
      };
      await updateSettings(payload);
      setStatus({ type: 'success', message: 'Settings updated successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setStatus({ type: 'error', message: error.response?.data?.message || 'Error saving settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        General Settings
      </Typography>

      {status.message && (
        <Alert severity={status.type} sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setStatus({ type: '', message: '' })}>
          {status.message}
        </Alert>
      )}

      <Paper sx={{ p: 4, borderRadius: '16px', boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Contact Information
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Support Email"
              name="email"
              value={settings.email}
              onChange={handleChange}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Phone Number"
              name="contactNumber"
              value={settings.contactNumber}
              onChange={handleChange}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={settings.address}
              onChange={handleChange}
              multiline
              rows={3}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Social Media Links
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Facebook URL"
              name="facebook"
              value={settings.facebook}
              onChange={handleChange}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="LinkedIn URL"
              name="linkedin"
              value={settings.linkedin}
              onChange={handleChange}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Twitter/X URL"
              name="twitter"
              value={settings.twitter}
              onChange={handleChange}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Instagram URL"
              name="instagram"
              value={settings.instagram}
              onChange={handleChange}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Feature Management
        </Typography>
        <Box sx={{ bgcolor: '#F8F9FA', p: 2, borderRadius: '12px' }}>
          <FormControlLabel
            control={
              <Switch 
                checked={settings.faqActive} 
                onChange={(e) => setSettings(prev => ({ ...prev, faqActive: e.target.checked }))}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#F25828' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#F25828' }
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>Enable FAQ Section</Typography>
                <Typography variant="body2" color="text.secondary">Show or hide the FAQ section on the public website.</Typography>
              </Box>
            }
          />
        </Box>

        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Technical Support Management
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Support Phone Number"
              name="supportPhone"
              value={settings.supportPhone}
              onChange={handleChange}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Support Email Address"
              name="supportEmail"
              value={settings.supportEmail}
              onChange={handleChange}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />
        
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Category Management
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                fullWidth
                label="New Category Name"
                value={newCategory.name}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                variant="outlined"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={newCategory.type}
                  onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value })}
                  label="Type"
                >
                  <MenuItem value="Blog">Blog Only</MenuItem>
                  <MenuItem value="News">News Only</MenuItem>
                  <MenuItem value="Both">Both (Blog & News)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAddCategory}
                disabled={categoryLoading || !newCategory.name.trim()}
                startIcon={categoryLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                sx={{
                  bgcolor: '#F25828',
                  '&:hover': { bgcolor: '#d44a20' },
                  borderRadius: '10px',
                  textTransform: 'none',
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Add Category
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {categories.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No categories found. Add one above.</Typography>
          ) : (
            categories.map((cat) => (
              <Chip
                key={cat.id}
                label={`${cat.name} (${cat.type})`}
                onDelete={() => handleDeleteCategory(cat.id)}
                deleteIcon={<DeleteIcon sx={{ color: '#F25828 !important' }} />}
                variant="outlined"
                sx={{ 
                  borderRadius: '8px', 
                  borderColor: '#E0E0E0',
                  px: 1,
                  py: 2,
                  '& .MuiChip-label': { fontWeight: 500 }
                }}
              />
            ))
          )}
        </Box>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: '#F25828',
              '&:hover': { bgcolor: '#d44a20' },
              borderRadius: '10px',
              textTransform: 'none',
              px: 4,
              py: 1,
              fontWeight: 600,
            }}
          >
            {saving ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Save Settings'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminSettings;
